import { useCallback, useEffect, useRef, useState } from 'react';
import { useTourSeen } from '@hooks/useTourSeen';
import { useTourRequest } from './tourRegistry';

/** The page's layout settles (fonts, a board's columns) before the first
 *  hole is measured. */
const SETTLE_MS = 500;

interface IUsePageTourArgs {
  /** The page's data is in and its panels are rendered. */
  ready: boolean;
  /** Whatever a dismissal must undo on the page — close the dialog or drawer
   *  an action step opened. Runs on every dismissal, at any step. */
  onDismiss: () => void;
}

/**
 * When a page's tour is open. The page owns the script and the step context;
 * this is the lifecycle every page shares.
 *
 * Opens by itself once: when the page is ready and the user has not seen it
 * (a failed seen-state read counts as seen — see `useTourSeen`). Opens again
 * on every replay request raised from the profile menu WHILE this page is
 * mounted; `key` changes with the request so the tour remounts at its first
 * step. Any dismissal marks it seen.
 */
export function usePageTour(tourId: string, { ready, onDismiss }: IUsePageTourArgs): {
  open: boolean;
  dismiss: () => void;
  key: number;
} {
  const { state, markSeen } = useTourSeen(tourId);
  const request = useTourRequest(tourId);
  const [open, setOpen] = useState(false);
  const autoStarted = useRef(false);
  // The replay store's nonce is process-wide and never resets, so a request
  // raised on an EARLIER visit to this page is still the latest one when the
  // page mounts again. Seeding the high-water mark with the value at mount is
  // what keeps that stale request from reopening the tour unasked.
  const handledRequest = useRef(request);

  useEffect(() => {
    if (!ready || state !== 'unseen' || autoStarted.current) return;
    // The guard is armed when the tour actually opens, not when the timer is
    // scheduled: StrictMode's double-invoke tears this effect down and runs it
    // again, and an already-set ref would swallow the re-armed timer.
    const timer = setTimeout(() => { autoStarted.current = true; setOpen(true); }, SETTLE_MS);
    return () => clearTimeout(timer);
  }, [ready, state]);

  useEffect(() => {
    if (request <= handledRequest.current || !ready) return;
    handledRequest.current = request;
    // Reopening IS synchronizing with an external system: the profile menu's
    // nonce lives outside React, and the high-water mark above makes this
    // fire at most once per request.
    setOpen(true);
  }, [request, ready]);

  const dismiss = useCallback(() => {
    setOpen(false);
    onDismiss();
    if (state !== 'seen') markSeen();
  }, [onDismiss, markSeen, state]);

  return { open, dismiss, key: request };
}
